<template>
  <transition mode="out-in">
  <!-- Skeleton no order -->
  <div v-if="!localOrder && !props.orderId"
       ref="cardContainer"
       class="card order-card skeleton-card">
    <div class="skeleton">
      <div class="skeleton-image"></div>
      <div :class="{'skeleton-text': !text, 'card-text fw-bold': text}">{{ text }}</div>
    </div>
  </div>

  <!-- Skeleton loading state -->
  <div v-else-if="!localOrder && props.orderId"
       ref="cardContainer"
       class="card order-card skeleton-card">
    <div class="skeleton-loading">
      <div class="skeleton-image"></div>
      <div class="skeleton-text"></div>
    </div>
  </div>

  <!-- Loaded order card -->
  <div v-else-if="localOrder"
       ref="cardContainer"
       class="card order-card"
       :class="{'new-order': newOrder, 'cancelled-order': localOrder.order_status==='Cancelled', 'returned-order': localOrder.order_status==='Returned', 'hidden-order': localOrder.is_hidden}"
       style="cursor: pointer;"
       @click.stop="onCallback"
  >

    <div class="row g-0">
      <div class="col-5 col-md-4">

        <lazy-image v-if="localOrder.remote_id"
                    :image-src="`/api/inventory/image/remote/${localOrder.remote_id}?size=thumb`"
                    class="card-img img-fluid"
                    :title="localOrder.title"/>
        <lazy-image v-else-if="localOrder.photo_url"
                    :image-src="localOrder.photo_url"
                    class="card-img img-fluid"
                    :title="localOrder.title"/>
      </div>
      <div class="col-7 col-md-8">
        <div class="card-body d-flex flex-column p-2">
            <div class="h-100">
              <h3 class="text-muted card-title d-flex align-items-end mb-2">
                <span>{{ $localDateTime(localOrder.order_date).slice(0,10) }}</span>
                <cross-listing-gate>
                  <platform-icon :platform="localOrder.remote_store" :size="24" class="ms-auto" />
                </cross-listing-gate>
              </h3>
              <h2 class="card-title three-line-ellipsis">
                {{ localOrder.line_items.length > 1 ? 'BUNDLE' : localOrder.item_name }}
              </h2>
          </div>
          <div v-if="localOrder.order_status === 'Completed'" class="d-flex align-items-center small">
            <div class="me-2">
              <span class="me-1 text-muted small">Sale:</span>
              <strong><money-block v-if="localOrder.order_status !== 'Cancelled'" :dollar="parseFloat(localOrder.sales_total || localOrder.sold_for)" class="d-inline" /></strong>
            </div>
            <div class="ms-auto"><span class="small text-muted"><span class="pending">~</span>Net:</span></div>
            <strong><money-block class="ms-1" :class="{ 'fst-italic text-muted': newOrder }" :dollar="netAmount" /></strong>
          </div>
        </div>
      </div>
    </div>
    <div class="new-banner shadow shadow-sm" title="SHIPPING UNASSIGNED">
      <i class="bi bi-truck"></i>
    </div>
    <div class="cancelled-banner">CANCELLED</div>
    <div class="returned-banner">RETURNED</div>
    <div class="hidden-banner">HIDDEN</div>
    <app-modal :modal-in="show" modal-class="modal-xl" @close="show=false">
      <template #header>Order Details {{ localOrder.order_id }}</template>
      <div class="container">
        <platform-order :order-id="localOrder.order_id" @hidden="handleHidden" @edit="handleEdit" @order-changed="c => handleOrderChanged(c)" />
      </div>
    </app-modal>
    <app-modal :modal-in="showEditModal" @close="showEditModal=false" modal-class="modal-xl">
      <template #header>Edit Order {{ localOrder.order_id }}</template>
      <create-order-flow
        v-if="showEditModal"
        :edit-order="editOrderData"
        @saved="handleEditSaved"
      />
    </app-modal>
  </div>
  </transition>
</template>
<script setup>
import { defineProps, defineEmits, ref, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import MoneyBlock from "@/app/components/Utils/MoneyBlock.vue";
import PlatformOrder from "@/app/components/ebay/PlatformOrder.vue";
import AppModal from "@/app/components/Utils/AppModal.vue";
import LazyImage from "@/app/components/Utils/LazyImage.vue";
import PlatformIcon from "@/app/components/Utils/PlatformIcon.vue";
import CrossListingGate from "@/app/components/Utils/CrossListingGate.vue";
import CreateOrderFlow from "@/app/components/orders/CreateOrderFlow.vue";

const store = useStore();
const emit = defineEmits(['order-changed'])
const show = ref(false)
const showEditModal = ref(false)
const editOrderData = ref(null)
const loading = ref(false)
const thisOrder = ref()
const props = defineProps(['order', 'orderId', 'callback', 'text'])
const localOrder = computed(() => props.order || thisOrder.value)
const newOrder = computed(() =>  localOrder.value.order_status==='Completed' && !localOrder.value.is_shipped)

// IntersectionObserver refs for lazy loading
const cardContainer = ref(null)
const isIntersecting = ref(false)
let observer = null

const netAmount = computed(() => {
  return parseFloat(localOrder.value?.is_shipped ? localOrder.value?.real_net : localOrder.value?.net)
})

function handleHidden() {
  show.value = false
  emit('order-changed')
}

function handleOrderChanged(source_change_only) {
  emit('order-changed', source_change_only || false)
}

function handleEdit(orderData) {
  show.value = false
  editOrderData.value = orderData
  showEditModal.value = true
}

function handleEditSaved() {
  showEditModal.value = false
  editOrderData.value = null
  emit('order-changed')
}

// Load order data function
const loadOrderData = () => {
  if (!thisOrder.value && !loading.value) {
    loading.value = true

    store.dispatch("eshop/getOrderById", { orderId: props.orderId })
      .then(o => {
        thisOrder.value = o.length ? o[0] : null
        loading.value = false
        if (observer) observer.disconnect()
      })
      .catch(err => {
        alert(err.response?.error?.message || err.message || JSON.stringify(err))
        loading.value = false
      })
  }
}

function onCallback () {
  if (props.callback && typeof props.callback === "function") {
    props.callback()
    return
  }
  show.value = true
}

// IntersectionObserver handler
const handleIntersect = (entries) => {
  if (entries[0].isIntersecting) {
    isIntersecting.value = true
    loadOrderData()
  }
}

// Setup observer on mount
onMounted(() => {
  if (props.orderId && !props.order) {
    observer = new IntersectionObserver(handleIntersect, {
      root: null,
      threshold: 0.1,
      rootMargin: '200px'
    })

    if (cardContainer.value) {
      observer.observe(cardContainer.value)
    }
  }
})

// Cleanup observer on unmount
onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<style lang="scss" scoped>
.skeleton-card {
  min-height: 85px;
  background: #f8f9fa;

  .skeleton,
  .skeleton-loading {
    display: flex;
    padding: 1rem;
    gap: 1rem;

    .skeleton-image {
      width: 40%;
      height: 85px;
      background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
      background-size: 200% 100%;
      border-radius: 4px;
    }

    .skeleton-text {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      &::before,
      &::after {
        content: '';
        height: 20px;
        background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
        background-size: 200% 100%;
        border-radius: 4px;
      }

      &::after {
        width: 60%;
      }
    }
  }
  .skeleton-loading {
    .skeleton-image {
      animation: loading 1.5s infinite;
    }

    .skeleton-text {
      &::before,
      &::after {
        animation: loading 1.5s infinite;
      }
    }
  }
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
